/* @flow */
import React, { Component } from "react";
import classnames from "classnames";
import List from "@raise/list";
import Button from "@raise/button";
import Icon from "@raise/icon";
import Badge from "@raise/badge";
import OutsideClickBoundary from "@raise/outside-click-boundary";
import { CartContext } from "@raise/with-cart-context-provider";
import type { Card } from "@raise/with-cart-context-provider";
import type { Node } from "react";

import styles from "./styles.scss";

export type CartMenuInternalProps = {
  className?: string,
  cards: Array<Card>,
  isOpen: boolean,
  dataset?: { [string]: any },
  onClick: MouseEvent => void,
  onRemoveCard: any => void,
  isCartLoading: boolean,
  errorMessage: string | null,
  totalValue: string,
  totalPrice: string,
  children: Node
};

const cartMenuDefaultProps = {
  className: "",
  cards: [],
  isOpen: false,
  dataset: {},
  onClick: () => {},
  onRemoveCard: () => {},
  isCartLoading: false,
  totalValue: "$0.00",
  totalPrice: "$0.00"
};

/**
 * @namespace CartMenu
 */

export class CartMenuInternal extends Component<CartMenuInternalProps> {
  static defaultProps = cartMenuDefaultProps;

  isMobile = window.matchMedia("(max-width: 766px)").matches

  constructor(props: CartMenuInternalProps) {
    super(props);
    this.renderCard = this.renderCard.bind(this);
    this.removeCard = this.removeCard.bind(this);
  }

  goToCart() {
    window.location.assign("/cart");
  }

  removeCard: Card => void;
  removeCard({id, isAvailable}: Card) {
    this.props.onRemoveCard({
      lineItemId: id,
      isAvailable,
      isUnifiedCart: false
    })
  }

  renderCard: Card => Node;
  renderCard(card: Card) {

    const {
      cardUrl,
      cardTypes,
      imageAlt,
      imageUrl,
      name,
      id,
      value,
      price
    } = card;

    return (
      <div className={styles.item}>
        <div>
          <a href={cardUrl} rel="noopener noreferrer">
            <img
              className={styles.cardImage}
              draggable="false"
              alt={imageAlt}
              src={imageUrl}
            />
          </a>
        </div>
        <div className={styles.description}>
          <a className={styles.cardName} href={cardUrl}>
            {name}
          </a>
          <div className={styles.cardTypes}>
            {cardTypes.map(c => (
              <Badge key={c.content} content={c.content} mobile={this.isMobile} />
            ))}
          </div>
          <span className={styles.cardValue}>{`${value} for ${price}`}</span>
        </div>
        <Button
          dataset={{
            "data-testid": "remove-cart-item-button",
            "data-logid": "remove-cart-item-cart-menu-button",
            "data-gtm": "true",
            "data-payload": id
          }}
          onClick={() => this.removeCard(card)}
          className={styles.remove}
        >
          <img
            alt="trash can icon"
            className={styles.trash}
            src="https://assets.raise.com/assets/trash.svg"
          />
        </Button>
      </div>
    );
  }

  renderError(errorMessage: string) {
    return (
      <div className={styles.errorArea}>
        <div className={styles.circle}>!</div>
        <span className={styles.error}>{errorMessage}</span>
      </div>
    );
  }

  renderLoadingOverlay() {
    return (
      <div className={styles.loadingOverlay}>
        <h2 className={styles.loadingDots}>Loading</h2>
      </div>
    );
  }

  render() {
    const {
      isCartLoading,
      className,
      children,
      dataset,
      onClick,
      isOpen,
      cards,
      errorMessage,
      totalValue,
      totalPrice
    } = this.props;
    const listStyles = {
      list: styles.list
    };

    const errorArea = errorMessage ? this.renderError(errorMessage) : null;
    const loadingArea = isCartLoading ? this.renderLoadingOverlay() : null;
    const cartMenuReviewButton = errorMessage
      ? "EDIT CART TO CONTINUE"
      : (this.isMobile ? "CHECKOUT" : "REVIEW AND CHECKOUT");
    const emptyArea = (
      <span className={styles.emptyArea}>Your cart is empty.</span>
    );

    const menu = (
      <div className={styles.content}>
        <List
          classNames={listStyles}
          items={cards}
          renderItem={this.renderCard}
        />
        <div className={styles.statusArea}>
          {loadingArea}
          <span className={styles.subtotal}>
            <span>{`Subtotal ${totalValue} for`} <span className={styles.totalPrice}>{totalPrice}</span></span>
          </span>
          <div className={styles.guarantee}>
            <Icon
              className={styles.guaranteeIcon}
              kind="guaranteeBannerOutline"
            />
            <span className={styles.guaranteeText}>
              1 Year Money-Back Guarantee
            </span>
          </div>
        </div>
        <Button
          isDisabled={isCartLoading || !!errorMessage}
          dataset={{
            "data-testid": "review-cart-button",
            "data-logid": "review-and-checkout-cart-menu-button",
            "data-gtm": "true"
          }}
          onClick={isCartLoading || errorMessage ? () => {} : this.goToCart}
          className={classnames(styles.button, styles.buttonWithoutPadding)}
          kind="primary"
          size="normal"
        >
          {cartMenuReviewButton}
        </Button>
      </div>
    );

    return (
      <OutsideClickBoundary
        className={styles.cartMenu}
        onClick={isOpen ? onClick : () => {}}
        {...dataset}
      >
        <div className={classnames(styles.cartMenu, className)}>
          <Button
            dataset={{
              "data-testid": "cart-menu-button",
              "data-logid": "cart-menu-button"
            }}
            className={styles.unsetButton}
            onClick={onClick}
          >
            {cards.length > 0 && (
              <span className={styles.itemCount}>{cards.length}</span>
            )}
            <div className={classnames(styles.cart)}>{children}</div>
          </Button>
          <div
            className={classnames(styles.popover, {
              [styles.popoverClosed]: !isOpen
            })}
          >
            <div className={styles.dropdown}>
              {errorArea}
              {cards.length < 1 ? emptyArea : menu}
            </div>
          </div>
        </div>
      </OutsideClickBoundary>
    );
  }
}

export type CartMenuProps = {
  className?: string,
  dataset?: { [string]: any },
  children: Node
};

const cartMenuInternalDefaultProps = {
  className: "",
  dataset: {}
};

const CartMenu = (props: CartMenuProps) => (
  <CartContext.Consumer>
    {({
      cards,
      isCartOpen,
      toggleCart,
      removeCard,
      totalValue,
      totalPrice,
      errorMessage,
      isCartLoading
    }) => (
      <CartMenuInternal
        isOpen={isCartOpen}
        onClick={toggleCart}
        cards={cards}
        onRemoveCard={removeCard}
        totalValue={totalValue}
        totalPrice={totalPrice}
        errorMessage={errorMessage}
        isCartLoading={isCartLoading}
        {...props}
      />
    )}
  </CartContext.Consumer>
);

CartMenu.defaultProps = cartMenuInternalDefaultProps;

export default CartMenu;
